import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Loader2, Save, Plus, Trash2, Droplets, MapPin } from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { api } from "../lib/api";
import { WATER_TYPES, DOG_RULES, shouldShowVenueName, shouldShowDogRules } from "./WaterSourcesSection";

// Distinct start/finish pins for the dual-endpoint picker — identical
// visual language to AdminAddTrail so operators build muscle memory
// across the add-new and edit flows.
const startIcon = L.divIcon({
  className: "",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  html: `<div class="td-marker" style="background:#3F5D44;color:#F6EFE0;">🏁</div>`,
});
const finishIcon = L.divIcon({
  className: "",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  html: `<div class="td-marker" style="background:#B16B3C;color:#F6EFE0;">🎯</div>`,
});

/** Inner map child that captures single-clicks and reports lat/lng back up
 *  the tree. Kept as its own component because `useMapEvents` has to live
 *  inside the <MapContainer> subtree. */
function ClickToSetCoords({ onPick }) {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
}

const RATINGS = [
  { value: "accessible", label: "Dog-friendly route" },
  { value: "difficult", label: "Stiles on route" },
  { value: "restricted", label: "Dogs not permitted" },
  { value: "unknown", label: "Unknown" },
];

const TYPES = [
  { value: "hiking", label: "Hiking trail" },
  { value: "camping", label: "Campsite" },
];

const STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

/**
 * Full-surface admin edit modal for any location. Editable fields cover
 * the admin/locations PATCH endpoint exactly.
 */
export default function AdminLocationEditModal({ location, onClose, onSaved }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: location.name || "",
    type: location.type || "hiking",
    region: location.region || "",
    short_desc: location.short_desc || "",
    lat: location.lat ?? "",
    lng: location.lng ?? "",
    address: location.address || "",
    google_maps_url: location.google_maps_url || "",
    point_latitude: location.point_latitude ?? "",
    point_longitude: location.point_longitude ?? "",
    dog_friendly_rating: location.dog_friendly_rating || "unknown",
    water_source_available: !!location.water_source_available,
    ozzy_verified: !!location.ozzy_verified,
    editor_pick: !!location.editor_pick,
    hidden: !!location.hidden,
    status: location.status || "approved",
    // Trail start / finish. Loaded from the saved sub-docs when present so
    // the picker map initialises on the actual trailhead, not a default.
    start_latitude: location.start_location?.lat ?? "",
    start_longitude: location.start_location?.lng ?? "",
    finish_latitude: location.finish_location?.lat ?? "",
    finish_longitude: location.finish_location?.lng ?? "",
  });

  const isCampsite = form.type === "camping";
  const isTrail = form.type === "hiking";
  // Which flag does the next click drop? Mirrors AdminAddTrail's pattern.
  const [pinMode, setPinMode] = useState("start");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const mut = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        lat: form.lat === "" ? null : parseFloat(form.lat),
        lng: form.lng === "" ? null : parseFloat(form.lng),
        point_latitude: form.point_latitude === "" ? null : parseFloat(form.point_latitude),
        point_longitude: form.point_longitude === "" ? null : parseFloat(form.point_longitude),
        // Trail endpoints — coerce blanks to null so the PATCH skips them
        // (see the `exclude_none` filter on LocationUpdate). Only send a
        // side when BOTH lat and lng are present so we never write a
        // half-set start_location sub-doc.
        start_latitude: form.start_latitude === "" ? null : parseFloat(form.start_latitude),
        start_longitude: form.start_longitude === "" ? null : parseFloat(form.start_longitude),
        finish_latitude: form.finish_latitude === "" ? null : parseFloat(form.finish_latitude),
        finish_longitude: form.finish_longitude === "" ? null : parseFloat(form.finish_longitude),
      };
      if (payload.start_latitude == null || payload.start_longitude == null) {
        payload.start_latitude = null; payload.start_longitude = null;
      }
      if (payload.finish_latitude == null || payload.finish_longitude == null) {
        payload.finish_latitude = null; payload.finish_longitude = null;
      }
      // strip nulls so PATCH skips them
      Object.keys(payload).forEach((k) => payload[k] == null && delete payload[k]);
      return (await api.patch(`/admin/locations/${location.location_id}`, payload)).data;
    },
    onSuccess: (data) => {
      toast.success("Location updated");
      qc.invalidateQueries({ queryKey: ["admin-locations"] });
      qc.invalidateQueries({ queryKey: ["admin-pending"] });
      onSaved?.(data.location);
      onClose?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't save"),
  });

  return (
    <div
      className="fixed inset-0 z-[1200] bg-brand-text/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="admin-loc-edit-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-brand-surface w-full sm:max-w-xl rounded-t-3xl sm:rounded-3xl p-6 animate-fade-up max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading text-xl text-brand-text">Edit location</h3>
          <button onClick={onClose} className="text-brand-subtle" aria-label="Close" data-testid="admin-loc-edit-close">
            <X size={20} />
          </button>
        </div>
        <p className="text-xs text-brand-subtle mb-4 break-all">{location.location_id} · {location.source}</p>

        <label className="block text-sm font-medium text-brand-text">Name</label>
        <input data-testid="loc-edit-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="td-input mt-1" />

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-sm font-medium text-brand-text">Type</label>
            <select data-testid="loc-edit-type" value={form.type} onChange={(e) => set("type", e.target.value)} className="td-input mt-1">
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-text">Status</label>
            <select data-testid="loc-edit-status" value={form.status} onChange={(e) => set("status", e.target.value)} className="td-input mt-1">
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <label className="block text-sm font-medium text-brand-text mt-3">Region</label>
        <input data-testid="loc-edit-region" value={form.region} onChange={(e) => set("region", e.target.value)} className="td-input mt-1" />

        <label className="block text-sm font-medium text-brand-text mt-3">Short description</label>
        <textarea data-testid="loc-edit-desc" value={form.short_desc} onChange={(e) => set("short_desc", e.target.value)} rows={3} maxLength={1000} className="td-input mt-1 resize-none" />

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-sm font-medium text-brand-text">Latitude</label>
            <input data-testid="loc-edit-lat" type="number" step="0.0001" value={form.lat} onChange={(e) => set("lat", e.target.value)} className="td-input mt-1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-text">Longitude</label>
            <input data-testid="loc-edit-lng" type="number" step="0.0001" value={form.lng} onChange={(e) => set("lng", e.target.value)} className="td-input mt-1" />
          </div>
        </div>

        {isCampsite && (
          <div className="mt-3 p-3 rounded-2xl bg-brand-sand/50 border border-brand-border/60" data-testid="loc-edit-campsite-fields">
            <div className="td-section-label">Campsite details</div>

            <label className="block text-sm font-medium text-brand-text mt-2">Address</label>
            <input
              data-testid="loc-edit-address"
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              maxLength={300}
              className="td-input mt-1 !text-sm"
            />

            <label className="block text-sm font-medium text-brand-text mt-3">Google Maps URL</label>
            <input
              data-testid="loc-edit-gmaps-url"
              value={form.google_maps_url}
              onChange={(e) => set("google_maps_url", e.target.value)}
              maxLength={600}
              placeholder="https://www.google.com/maps/place/..."
              className="td-input mt-1 !text-sm"
            />

            <div className="td-section-label mt-3">Manual Coordinates Override</div>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <input
                data-testid="loc-edit-point-lat"
                type="number"
                step="0.0001"
                value={form.point_latitude}
                onChange={(e) => set("point_latitude", e.target.value)}
                placeholder="Latitude"
                className="td-input !text-sm"
              />
              <input
                data-testid="loc-edit-point-lng"
                type="number"
                step="0.0001"
                value={form.point_longitude}
                onChange={(e) => set("point_longitude", e.target.value)}
                placeholder="Longitude"
                className="td-input !text-sm"
              />
            </div>
            <p className="text-xs text-brand-subtle mt-1.5">
              Manual coords win over the URL on save.
            </p>
          </div>
        )}

        {isTrail && (
          <div
            className="mt-4 p-3 rounded-2xl bg-brand-sand/50 border border-brand-border/60"
            data-testid="loc-edit-trail-endpoints"
          >
            <div className="td-section-label flex items-center gap-1.5">
              <MapPin size={11} className="text-brand-primary" /> Start &amp; finish
              {(location.start_source === "manual" || location.finish_source === "manual") && (
                <span className="text-[10px] font-normal text-brand-subtle ml-1">
                  (some pins hand-corrected)
                </span>
              )}
            </div>
            <p className="text-xs text-brand-subtle mt-1">
              Reposition either flag with a click — the mode toggle below decides which one moves. Saving here flags the touched side as <em>manual</em>.
            </p>

            <div
              className="mt-2 grid grid-cols-2 gap-2"
              data-testid="loc-edit-pin-mode-toggle"
            >
              <button
                type="button"
                data-testid="loc-edit-pin-mode-start"
                onClick={() => setPinMode("start")}
                className={`flex items-center justify-center gap-2 py-2 rounded-2xl border text-sm font-medium transition-colors ${
                  pinMode === "start"
                    ? "bg-brand-primary text-brand-sand border-brand-primary"
                    : "bg-brand-surface text-brand-text border-brand-border hover:border-brand-primary/40"
                }`}
              >
                🏁 Setting start
              </button>
              <button
                type="button"
                data-testid="loc-edit-pin-mode-finish"
                onClick={() => setPinMode("finish")}
                className={`flex items-center justify-center gap-2 py-2 rounded-2xl border text-sm font-medium transition-colors ${
                  pinMode === "finish"
                    ? "bg-brand-accent text-brand-sand border-brand-accent"
                    : "bg-brand-surface text-brand-text border-brand-border hover:border-brand-accent/40"
                }`}
              >
                🎯 Setting finish
              </button>
            </div>

            <div
              data-testid="loc-edit-endpoints-map"
              className="td-card overflow-hidden h-56 mt-3"
            >
              <MapContainer
                // Prefer the saved start; fall back to saved finish; then
                // the doc-level lat/lng; only then reach for Snowdonia.
                // Keeps the map zoomed on the trail itself in every case.
                center={[
                  form.start_latitude !== "" ? parseFloat(form.start_latitude)
                    : form.finish_latitude !== "" ? parseFloat(form.finish_latitude)
                    : form.lat !== "" ? parseFloat(form.lat)
                    : 53.07,
                  form.start_longitude !== "" ? parseFloat(form.start_longitude)
                    : form.finish_longitude !== "" ? parseFloat(form.finish_longitude)
                    : form.lng !== "" ? parseFloat(form.lng)
                    : -4.08,
                ]}
                zoom={13}
                scrollWheelZoom={false}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="© OpenStreetMap contributors"
                />
                <ClickToSetCoords
                  onPick={(lat, lng) => {
                    setForm((s) => (
                      pinMode === "start"
                        ? { ...s, start_latitude: lat.toFixed(6), start_longitude: lng.toFixed(6) }
                        : { ...s, finish_latitude: lat.toFixed(6), finish_longitude: lng.toFixed(6) }
                    ));
                  }}
                />
                {form.start_latitude !== "" && form.start_longitude !== ""
                  && !isNaN(parseFloat(form.start_latitude))
                  && !isNaN(parseFloat(form.start_longitude)) && (
                  <Marker
                    position={[parseFloat(form.start_latitude), parseFloat(form.start_longitude)]}
                    icon={startIcon}
                  />
                )}
                {form.finish_latitude !== "" && form.finish_longitude !== ""
                  && !isNaN(parseFloat(form.finish_latitude))
                  && !isNaN(parseFloat(form.finish_longitude)) && (
                  <Marker
                    position={[parseFloat(form.finish_latitude), parseFloat(form.finish_longitude)]}
                    icon={finishIcon}
                  />
                )}
              </MapContainer>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="p-2 rounded-2xl bg-brand-primary/5 border border-brand-primary/30">
                <div className="td-section-label text-brand-primary">🏁 Start</div>
                <div className="grid grid-cols-2 gap-1.5 mt-1">
                  <input
                    data-testid="loc-edit-start-lat"
                    type="number"
                    step="0.000001"
                    value={form.start_latitude}
                    onChange={(e) => set("start_latitude", e.target.value)}
                    placeholder="Lat"
                    className="td-input !text-xs !py-1.5"
                  />
                  <input
                    data-testid="loc-edit-start-lng"
                    type="number"
                    step="0.000001"
                    value={form.start_longitude}
                    onChange={(e) => set("start_longitude", e.target.value)}
                    placeholder="Lng"
                    className="td-input !text-xs !py-1.5"
                  />
                </div>
              </div>
              <div className="p-2 rounded-2xl bg-brand-accent/5 border border-brand-accent/30">
                <div className="td-section-label text-brand-accent">🎯 Finish</div>
                <div className="grid grid-cols-2 gap-1.5 mt-1">
                  <input
                    data-testid="loc-edit-finish-lat"
                    type="number"
                    step="0.000001"
                    value={form.finish_latitude}
                    onChange={(e) => set("finish_latitude", e.target.value)}
                    placeholder="Lat"
                    className="td-input !text-xs !py-1.5"
                  />
                  <input
                    data-testid="loc-edit-finish-lng"
                    type="number"
                    step="0.000001"
                    value={form.finish_longitude}
                    onChange={(e) => set("finish_longitude", e.target.value)}
                    placeholder="Lng"
                    className="td-input !text-xs !py-1.5"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <label className="block text-sm font-medium text-brand-text mt-3">Dog-friendliness</label>
        <select data-testid="loc-edit-rating" value={form.dog_friendly_rating} onChange={(e) => set("dog_friendly_rating", e.target.value)} className="td-input mt-1">
          {RATINGS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>

        <div className="grid grid-cols-2 gap-2 mt-4">
          {[
            ["ozzy_verified", "Ozzy Verified"],
            ["editor_pick", "Editor's pick"],
            ["water_source_available", "Water source"],
            ["hidden", "Hidden from public"],
          ].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 px-3 py-2 rounded-2xl border border-brand-border cursor-pointer">
              <input data-testid={`loc-edit-${k}`} type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} className="w-4 h-4 accent-brand-accent" />
              <span className="text-sm text-brand-text">{label}</span>
            </label>
          ))}
        </div>

        <div className="flex gap-2 mt-5">
          <button data-testid="loc-edit-cancel" type="button" onClick={onClose} className="td-btn-secondary flex-1 !py-2.5">Cancel</button>
          <button
            data-testid="loc-edit-save"
            type="button"
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            className="td-btn-primary flex-1 !py-2.5"
          >
            {mut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save changes
          </button>
        </div>

        {/* Water source manager */}
        <WaterSourceManager locationId={location.location_id} />
      </div>
    </div>
  );
}

function WaterSourceManager({ locationId }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState("");
  const [newVenue, setNewVenue] = useState("");
  const [newDogRules, setNewDogRules] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const newShowVenue = shouldShowVenueName(newType);
  const newShowDogRules = shouldShowDogRules(newType);

  const list = useQuery({
    queryKey: ["admin-water", locationId],
    queryFn: async () => (await api.get(`/locations/${locationId}/water-sources`)).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-water", locationId] });
    qc.invalidateQueries({ queryKey: ["water-sources", locationId] });
  };

  const createMut = useMutation({
    mutationFn: () => {
      const payload = {
        location_id: locationId,
        water_type: newType,
        description: newDesc.trim() || undefined,
        is_available: true,
      };
      if (newShowVenue && newVenue.trim()) payload.venue_name = newVenue.trim();
      if (newShowDogRules && newDogRules) payload.dog_rules = newDogRules;
      return api.post(`/admin/water-sources`, payload);
    },
    onSuccess: () => {
      toast.success("Amenity added");
      setNewType(""); setNewVenue(""); setNewDogRules(""); setNewDesc(""); setAdding(false);
      refresh();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't add"),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/admin/water-sources/${id}`, payload),
    onSuccess: () => { toast.success("Changes saved"); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't save"),
  });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/admin/water-sources/${id}`),
    onSuccess: () => { toast.success("Deleted"); refresh(); },
    onError: () => toast.error("Couldn't delete"),
  });

  const items = list.data?.items || [];

  return (
    <div className="mt-6 pt-5 border-t border-brand-border/60" data-testid="admin-water-manager">
      <div className="flex items-center justify-between">
        <div className="td-section-label flex items-center gap-1.5">
          <Droplets size={13} className="text-brand-primary" /> Amenities · {items.length}
        </div>
        <button
          data-testid="admin-water-add-toggle"
          type="button"
          onClick={() => setAdding((v) => !v)}
          className="td-btn-secondary !py-1.5 !px-3 !text-xs"
        >
          <Plus size={12} /> {adding ? "Cancel" : "Add"}
        </button>
      </div>

      {adding && (
        <div className="mt-3 p-3 rounded-2xl border border-brand-border bg-brand-sand/50 space-y-2">
          <select data-testid="admin-water-new-type" value={newType} onChange={(e) => setNewType(e.target.value)} className="td-input !text-sm">
            <option value="">— Type —</option>
            {["Water", "Refreshments", "Facilities", "Other"].map((g) => (
              <optgroup key={g} label={g}>
                {WATER_TYPES.filter((t) => t.group === g).map((t) => (
                  <option key={t.value} value={t.value}>{t.emoji} {t.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          {newShowVenue && (
            <input
              data-testid="admin-water-new-venue"
              value={newVenue}
              onChange={(e) => setNewVenue(e.target.value)}
              maxLength={120}
              placeholder="Venue name (e.g. The Plough Inn)"
              className="td-input !text-sm"
            />
          )}
          {newShowDogRules && (
            <select
              data-testid="admin-water-new-dog-rules"
              value={newDogRules}
              onChange={(e) => setNewDogRules(e.target.value)}
              className="td-input !text-sm"
            >
              <option value="">— Dog policy (optional) —</option>
              {DOG_RULES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          )}
          <textarea
            data-testid="admin-water-new-desc"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            rows={2}
            maxLength={400}
            placeholder="How do you find it? (e.g., 'Bin behind the gate')"
            className="td-input !text-sm resize-none"
          />
          <button
            data-testid="admin-water-new-save"
            type="button"
            onClick={() => newType && createMut.mutate()}
            disabled={!newType || createMut.isPending}
            className="td-btn-primary !py-1.5 !px-3 !text-xs"
          >
            {createMut.isPending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
          </button>
        </div>
      )}

      {items.length > 0 ? (
        <ul className="mt-3 divide-y divide-brand-border/60" data-testid="admin-water-list">
          {items.map((w) => {
            const showVenue = shouldShowVenueName(w.water_type);
            const showDogRules = shouldShowDogRules(w.water_type);
            return (
              <li key={w.water_source_id} className="py-2.5 space-y-1.5" data-testid={`admin-water-row-${w.water_source_id}`}>
                <div className="flex items-start gap-2">
                  <select
                    data-testid={`admin-water-type-${w.water_source_id}`}
                    value={w.water_type}
                    onChange={(e) => patchMut.mutate({ id: w.water_source_id, payload: { water_type: e.target.value } })}
                    className="td-input !text-xs !w-auto !py-1.5"
                  >
                    {["Water", "Refreshments", "Facilities", "Other"].map((g) => (
                      <optgroup key={g} label={g}>
                        {WATER_TYPES.filter((t) => t.group === g).map((t) => (
                          <option key={t.value} value={t.value}>{t.emoji} {t.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <input
                    data-testid={`admin-water-desc-${w.water_source_id}`}
                    defaultValue={w.description || ""}
                    onBlur={(e) => {
                      const v = e.target.value;
                      if (v !== (w.description || "")) patchMut.mutate({ id: w.water_source_id, payload: { description: v } });
                    }}
                    placeholder="Description"
                    className="td-input flex-1 !text-xs !py-1.5"
                  />
                  <label className="flex items-center gap-1 text-[11px] text-brand-subtle whitespace-nowrap" title="Available right now?">
                    <input
                      data-testid={`admin-water-avail-${w.water_source_id}`}
                      type="checkbox"
                      checked={w.is_available}
                      onChange={(e) => patchMut.mutate({ id: w.water_source_id, payload: { is_available: e.target.checked } })}
                      className="w-4 h-4 accent-brand-primary"
                    />
                    avail
                  </label>
                  <button
                    data-testid={`admin-water-del-${w.water_source_id}`}
                    type="button"
                    onClick={() => delMut.mutate(w.water_source_id)}
                    className="text-brand-error p-1"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                {showVenue && (
                  <input
                    data-testid={`admin-water-venue-${w.water_source_id}`}
                    defaultValue={w.venue_name || ""}
                    onBlur={(e) => {
                      const v = e.target.value;
                      if (v !== (w.venue_name || "")) patchMut.mutate({ id: w.water_source_id, payload: { venue_name: v } });
                    }}
                    placeholder="Venue name"
                    className="td-input w-full !text-xs !py-1.5"
                  />
                )}
                {showDogRules && (
                  <select
                    data-testid={`admin-water-dog-${w.water_source_id}`}
                    value={w.dog_rules || ""}
                    onChange={(e) => patchMut.mutate({ id: w.water_source_id, payload: { dog_rules: e.target.value || null } })}
                    className="td-input w-full !text-xs !py-1.5"
                  >
                    <option value="">— Dog policy —</option>
                    {DOG_RULES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                )}
              </li>
            );
          })}
        </ul>
      ) : !list.isLoading ? (
        <div className="text-xs text-brand-subtle mt-3">No amenities on this location yet.</div>
      ) : null}
    </div>
  );
}
